import type { BalancePoint } from '@tahi/core';
import { copy } from '../copy';
import { dayMonth, peso } from '../format';

const W = 320;
const H = 130;
const PAD = 10;

const dayNum = (d: string) => Date.parse(`${d}T00:00:00Z`) / 86_400_000;

/**
 * Loan payoff curve (OPEN-QUESTIONS v0.5 Q8): the balance so far as a line, the projection as dots,
 * with a "Paid so far" / "Projected" legend. One labelled image; the numbers are in text nearby.
 */
export function PayoffChart({ points, ariaLabel }: { points: BalancePoint[]; ariaLabel: string }) {
  if (points.length < 2) return null;
  const xs = points.map((p) => dayNum(p.date));
  const minX = Math.min(...xs);
  const spanX = Math.max(...xs) - minX || 1;
  const maxY = Math.max(...points.map((p) => p.balanceMinor)) || 1;
  const x = (d: string) => PAD + ((dayNum(d) - minX) * (W - 2 * PAD)) / spanX;
  const y = (v: number) => PAD + ((maxY - v) * (H - 2 * PAD)) / maxY;
  const actual = points.filter((p) => !p.projected);
  const projected = points.filter((p) => p.projected);
  const path = actual
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.date).toFixed(1)},${y(p.balanceMinor).toFixed(1)}`)
    .join(' ');
  const first = points[0]!;
  const last = points[points.length - 1]!;
  return (
    <figure role="img" aria-label={ariaLabel} className="flex flex-col gap-2">
      <div className="flex gap-2">
        <div
          aria-hidden="true"
          className="flex w-16 shrink-0 flex-col justify-between py-1 text-right font-mono text-[11px] tabular-nums text-muted"
        >
          <span>{peso(maxY)}</span>
          <span>₱0</span>
        </div>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="min-w-0 flex-1"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <line x1={0} x2={W} y1={H - PAD} y2={H - PAD} stroke="var(--divider)" strokeWidth={1} />
          {actual.length > 1 ? (
            <path
              d={path}
              fill="none"
              stroke="var(--chart-1)"
              strokeWidth={2.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ) : null}
          {actual.length > 0 ? (
            <circle
              cx={x(actual[actual.length - 1]!.date)}
              cy={y(actual[actual.length - 1]!.balanceMinor)}
              r={4.5}
              fill="var(--accent)"
            />
          ) : null}
          {projected.map((p, i) => (
            <circle
              key={`${p.date}-${i}`}
              cx={x(p.date)}
              cy={y(p.balanceMinor)}
              r={2.5}
              fill="var(--muted)"
            />
          ))}
        </svg>
      </div>
      <div aria-hidden="true" className="ml-[72px] flex justify-between text-[11px] text-muted">
        <span>{dayMonth(first.date)}</span>
        <span>{`${dayMonth(last.date)} ${last.date.slice(0, 4)}`}</span>
      </div>
      <div aria-hidden="true" className="flex gap-4 text-xs text-muted">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-0.5 w-4 bg-[var(--chart-1)]" />
          {copy.paidSoFar}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--muted)]" />
          {copy.projected}
        </span>
      </div>
    </figure>
  );
}
